import tenant from '../config/tenant';
import { downloadAuthedFile, needsAuth } from './authedFile';


export function getEmployeeProfilePicUrl(emp) {
  if (!emp || typeof emp !== 'object') return null;

  // 1. Check direct IMAGEURL if non-empty and valid
  if (emp.IMAGEURL && String(emp.IMAGEURL).trim() && String(emp.IMAGEURL).trim() !== 'null') {
    return getFullDocumentUrl(String(emp.IMAGEURL).trim());
  }

  // 2. Check Documents array for ProfilePic doc type
  const docArrays = [emp.Documents, emp.documents, emp._Documents, emp.Docs, emp.docs].filter(Array.isArray);
  for (const arr of docArrays) {
    const profileDoc = arr.find(doc =>
      doc && (doc.DocType === 'ProfilePic' || /profile\s*pic/i.test(doc.DocType || '') || /profile/i.test(doc.DocType || ''))
    );
    if (profileDoc) {
      const url = profileDoc.URL || profileDoc.url || profileDoc.Path || profileDoc.path || profileDoc.FilePath;
      if (url && String(url).trim()) {
        return getFullDocumentUrl(String(url).trim());
      }
    }
  }

  // 3. Check fallbacks
  if (emp.imageUrl || emp.image_url || emp.p_image_url || emp.ProfilePic || emp.profilePic) {
    const fallback = emp.imageUrl || emp.image_url || emp.p_image_url || emp.ProfilePic || emp.profilePic;
    if (fallback && String(fallback).trim() !== 'null') {
      return getFullDocumentUrl(String(fallback).trim());
    }
  }

  return null;
}


const pickFirst = (emp, keys) => {
  if (!emp) return '';
  for (const k of keys) {
    const v = emp[k];
    if (v === null || v === undefined) continue;
    const t = String(v).trim();
    if (t && t !== 'null' && t !== 'undefined') return t;
  }
  return '';
};


export function getLocationName(emp) {
  return pickFirst(emp, [
    'HLD_LOCATION_NAME', 'HLD_LOC_NAME', 'HLD_LOC_DESC',
    'LocationName', 'locationName', 'HedLocationName',
  ]);
}

/** The accounting cost centre. */
export function getCostCenter(emp) {
  return pickFirst(emp, ['HED_COSTCENTER', 'HedCostCenter', 'CostCenter', 'costCenter', 'p_location_code']);
}

/**
 * Resolves full document URL from relative path or absolute URL.
 */
export function getFullDocumentUrl(urlOrPath) {
  if (!urlOrPath || typeof urlOrPath !== 'string') return null;
  const cleaned = urlOrPath.trim();
  if (!cleaned || cleaned === 'null' || cleaned === 'undefined') return null;

  // If already absolute or data URL
  if (/^(https?:|data:|\/\/)/i.test(cleaned)) {
    return cleaned;
  }

  const baseUrl = (tenant?.api?.baseUrl || '').replace(/\/+$/, '');
  const cleanPath = cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Triggers downloading a document file locally.
 *
 * The plain fetch here had no `auth-key` header, so every document endpoint
 * answered 401 and the code fell through to the anchor fallback — which is a
 * bare browser navigation and gets a 401 too. It now goes through
 * downloadAuthedFile, which attaches the key.
 */
export async function downloadFile(url, fileName) {
  if (!url) return;
  const targetName = fileName || 'document';

  if (needsAuth(url)) {
    await downloadAuthedFile(url, targetName);
    return;
  }

  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = targetName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.warn('[downloadFile] Blob fetch failed, using fallback download anchor:', err);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = targetName;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Extracts, parses and normalizes all document files for an employee record.
 */
export function extractEmployeeDocuments(emp) {
  if (!emp || typeof emp !== 'object') return [];

  const docsMap = new Map();

  const getFileExt = (url) => {
    try {
      const pathname = new URL(url, 'http://dummy.com').pathname;
      const ext = pathname.substring(pathname.lastIndexOf('.'));
      if (ext && ext.length <= 5 && ext.includes('.')) return ext.toLowerCase();
    } catch {}
    return '';
  };

  const addDoc = (key, category, title, rawUrl, fileNameOverride = null) => {
    if (!rawUrl) return;
    const fullUrl = getFullDocumentUrl(rawUrl);
    if (!fullUrl) return;

    if (!docsMap.has(key)) {
      let ext = getFileExt(fullUrl);
      if (!ext && fileNameOverride) {
        const m = String(fileNameOverride).match(/\.[a-zA-Z0-9]{1,5}$/);
        if (m) ext = m[0].toLowerCase();
      }
      const isPdf = ext === '.pdf';
      const isImg = /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(ext) || /^data:image/i.test(fullUrl);

      docsMap.set(key, {
        id: key,
        category, // 'nic' | 'birth_certificate' | 'passport' | 'qualification' | 'profile_pic' | 'other'
        title,
        url: fullUrl,
        rawPath: rawUrl,
        fileName: fileNameOverride || `${title.replace(/[^a-zA-Z0-9_\-]/g, '_')}${ext || (isPdf ? '.pdf' : isImg ? '.jpg' : '')}`,
        isPdf,
        isImg,
      });
    }
  };

  // 1. NIC Document
  const nicUrl =
    emp.HED_NIC_URL || emp.HED_NIC_PATH ||
    emp.p_nic_url || emp.p_nic_path ||
    emp.NicUrl || emp.NicPath || emp.nicUrl || emp.nicPath ||
    emp.nicDocument || emp.NICDocument || emp.NICCopy || emp.nicCopy ||
    emp.NIC || emp.nic_path || emp.nic_url;
  if (nicUrl) {
    addDoc('nic', 'nic', 'NIC Copy / Document', nicUrl);
  }

  // 2. Birth Certificate
  const birthCertUrl =
    emp.HED_BIRTH_CERTIFICATE_URL || emp.HED_BIRTH_CERTIFICATE_PATH ||
    emp.p_birth_certificate_url || emp.p_birth_certificate_path ||
    emp.BirthCertificateUrl || emp.BirthCertificatePath || emp.birthCertificateUrl || emp.birthCertificatePath ||
    emp.birthCertificate || emp.BirthCertificate || emp.birth_certificate || emp.birth_certificate_path || emp.birth_certificate_url;
  if (birthCertUrl) {
    addDoc('birth_certificate', 'birth_certificate', 'Birth Certificate', birthCertUrl);
  }

  // 3. Educational Qualification Certificate
  const qualCertUrl =
    emp.HED_QUALIFICATION_CERTIFICATE_URL || emp.HED_QUALIFICATION_CERTIFICATE_PATH ||
    emp.p_qualification_certificate_url || emp.p_qualification_certificate_path ||
    emp.QualificationCertificateUrl || emp.QualificationCertificatePath ||
    emp.qualificationCertificate || emp.QualificationCertificate ||
    emp.qualification_certificate || emp.EducationCertificate || emp.qualificationDocument;
  if (qualCertUrl) {
    addDoc('qualification', 'qualification', 'Educational Qualification Certificate', qualCertUrl);
  }

  // 4. Passport Document
  const passportUrl =
    emp.HED_PASSPORT_URL || emp.HED_PASSPORT_PATH ||
    emp.p_passport_url || emp.p_passport_path ||
    emp.PassportUrl || emp.PassportPath || emp.passportUrl || emp.passportPath ||
    emp.passportDocument || emp.PassportDocument || emp.passport_copy;
  if (passportUrl) {
    addDoc('passport', 'passport', 'Passport Copy', passportUrl);
  }

  // 5a. Trainee Certificate
  const traineeCertUrl =
    emp.HED_TRAINEE_CERTIFICATE_URL || emp.HED_TRAINEE_CERTIFICATE_PATH ||
    emp.p_trainee_certificate_url || emp.p_trainee_certificate_path ||
    emp.TraineeCertificateUrl || emp.TraineeCertificatePath ||
    emp.traineeCertificate || emp.TraineeCertificate || emp.trainee_certificate;
  if (traineeCertUrl) {
    addDoc('trainee_certificate', 'trainee_certificate', 'Trainee Certificate', traineeCertUrl);
  }

  // 5b. Other Certificates
  const otherCertUrl =
    emp.HED_OTHER_CERTIFICATE_URL || emp.HED_OTHER_CERTIFICATE_PATH ||
    emp.p_other_certificate_url || emp.p_other_certificate_path ||
    emp.OtherCertificateUrl || emp.OtherCertificatePath ||
    emp.otherCertificate || emp.OtherCertificate || emp.other_certificate;
  if (otherCertUrl) {
    addDoc('other_certificate', 'other_certificate', 'Other Certificates', otherCertUrl);
  }

  // 6. Check `emp.Documents` / `emp.documents` / `emp._Documents` / `emp.Docs` / `emp.docs`
  const docArrays = [emp.Documents, emp.documents, emp._Documents, emp.Docs, emp.docs].filter(Array.isArray);
  docArrays.forEach(arr => {
    arr.forEach((dItem, idx) => {
      if (!dItem || typeof dItem !== 'object') return;
      const typeStr = String(dItem.DocType || dItem.docType || dItem.HED_DOC_TYPE || dItem.Type || dItem.type || 'Document').trim();
      const url = dItem.URL || dItem.url || dItem.HED_DOC_URL || dItem.Path || dItem.path || dItem.FilePath || dItem.filePath;
      const fileNameRaw = dItem.FileName || dItem.fileName || dItem.DocName || dItem.docName || null;
      const name = fileNameRaw || typeStr;

      let key = `doc_${idx}_${typeStr.toLowerCase().replace(/\s+/g, '_')}`;
      let category = 'other';
      if (/nic/i.test(typeStr)) { key = 'nic'; category = 'nic'; }
      else if (/birth/i.test(typeStr)) { key = 'birth_certificate'; category = 'birth_certificate'; }
      else if (/trainee/i.test(typeStr)) { key = 'trainee_certificate'; category = 'trainee_certificate'; }
      else if (/^other/i.test(typeStr)) { key = 'other_certificate'; category = 'other_certificate'; }
      else if (/qualif|edu|degree|diploma|cert/i.test(typeStr)) { key = 'qualification'; category = 'qualification'; }
      else if (/passport/i.test(typeStr)) { key = 'passport'; category = 'passport'; }
      else if (/profile/i.test(typeStr)) { key = 'profile_pic'; category = 'profile_pic'; }

      if (url) {
        addDoc(key, category, name || typeStr, url, fileNameRaw);
      }
    });
  });

  // 5. Profile Picture (fallback — the Documents array scan above already
  const profilePicUrl = getEmployeeProfilePicUrl(emp);
  if (profilePicUrl) {
    addDoc('profile_pic', 'profile_pic', 'Profile Photo', profilePicUrl);
  }

  // 7. Qualifications attachments
  const qualArrays = [emp.Qualifications, emp._Qualifications, emp.qualifications, emp.Educations, emp._Educations].filter(Array.isArray);
  qualArrays.forEach(arr => {
    arr.forEach((q, idx) => {
      if (!q || typeof q !== 'object') return;
      const attUrl = q.HEQ_ATTACHMENT || q.heq_attachment || q.Attachment || q.attachment || q.p_heqAttachment;
      const qualName = q.HEQ_QUALIFICATION_NAME || q.QualificationName || q.qualificationName || `Qualification #${idx + 1}`;
      if (attUrl) {
        addDoc(`qual_${idx}`, 'qualification', `Educational Document (${qualName})`, attUrl);
      }
    });
  });

  return Array.from(docsMap.values());
}